import React from 'react';
import {
  Typography as TypographyBase,
  TypographyProps as MuiTypographyProps,
} from '@mui/material';
import { TypographyOptions } from '@mui/material/styles/createTypography';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import palette, { PaletteToken, SemanticText } from '@ui/palette';

// Extend TypographyOptions to include our custom variants
declare module '@mui/material/styles/createTypography' {
  interface TypographyOptions {
    pLg: TypographyStyleOptions;
    pMd: TypographyStyleOptions;
    pSm: TypographyStyleOptions;
    pXs: TypographyStyleOptions;
    labelMd: TypographyStyleOptions;
    labelSm: TypographyStyleOptions;
  }
}

// Also extend the TypographyProps variant type
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    pLg: true;
    pMd: true;
    pSm: true;
    pXs: true;
    labelMd: true;
    labelSm: true;
  }
}

const defaultFontFamily = `"Assistant", sans-serif`;
const dmSansFontFamily = `"DM Sans", sans-serif`;

const weights = {
  regular: 400,
  bold: 700,
};

export const headerCommon = {
  fontFamily: dmSansFontFamily,
  color: palette.semantic.text.primary,
};

function pxToLineHeight(px: number, baseFontSize: number = 16): number {
  return Number((px / baseFontSize).toFixed(4));
}

/**
 * @todo Consider extending the typography options to support the following: (vs leveraging body1 and body2)
 * - pLg, pMd, pSm, pXs
 * - labelSm, labelMd, labelXs
 *
 * @see https://www.figma.com/design/EqditHsVwk24eL6Kor20H3/Breve-Web?node-id=6-270&t=NZ5oRvBUIZ4vOf4k-0
 */
const typography: TypographyOptions = {
  htmlFontSize: 16,
  fontFamily: defaultFontFamily,
  h1: {
    fontSize: `2rem`, // 32px
    lineHeight: pxToLineHeight(42, 32),
    ...headerCommon,
  },
  h2: {
    fontSize: `1.5rem`, // 24px
    lineHeight: pxToLineHeight(31, 24), // 31px (24 * 1.2917 = 31)
    ...headerCommon,
  },
  h3: {
    fontSize: `1rem`, // 16px
    lineHeight: pxToLineHeight(21, 16),
    ...headerCommon,
  },
  h4: {
    fontSize: `0.875rem`, // 14px
    lineHeight: pxToLineHeight(18, 14),
    ...headerCommon,
  },
  h5: {
    fontSize: `0.75rem`, // 12px
    lineHeight: pxToLineHeight(16, 12),
    ...headerCommon,
  },
  pLg: {
    fontSize: `1rem`, // 16px
    lineHeight: pxToLineHeight(21, 16),
    fontFamily: defaultFontFamily,
    color: palette.semantic.text.primary,
  },
  pMd: {
    fontSize: `0.875rem`, // 14px
    lineHeight: pxToLineHeight(18, 14),
    fontFamily: defaultFontFamily,
    color: palette.semantic.text.primary,
  },
  pSm: {
    fontSize: `0.75rem`, // 12px
    lineHeight: pxToLineHeight(16, 12),
    fontFamily: defaultFontFamily,
    color: palette.semantic.text.primary,
  },
  pXs: {
    fontSize: `0.689rem`,
    lineHeight: pxToLineHeight(14, 11),
    fontFamily: defaultFontFamily,
    color: palette.semantic.text.primary,
  },
  labelMd: {
    fontSize: `0.75rem`, // 12px
    lineHeight: pxToLineHeight(16, 12),
    fontFamily: dmSansFontFamily,
    color: palette.semantic.text.primary,
  },
  labelSm: {
    fontSize: `0.675rem`, // 11px
    lineHeight: pxToLineHeight(14, 11),
    fontFamily: dmSansFontFamily,
    color: palette.semantic.text.primary,
  },
};

export { typography };

const useStyles = makeStyles({ name: 'CustomTypography' })(theme => ({
  underlined: {
    textDecoration: 'underline',
  },
  uppercased: {
    textTransform: 'uppercase',
  },
  inline: {
    display: 'inline',
  },
}));

export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'pLg'
  | 'pMd'
  | 'pSm'
  | 'pXs'
  | 'labelMd'
  | 'labelSm';

function getColor(token?: string): string {
  if (!token) {
    return palette.semantic.text.primary;
  }
  if (token === 'inherit') {
    return 'inherit';
  }
  if (token in palette.semantic.text) {
    return palette.semantic.text[token as SemanticText];
  }
  if (token in palette.tokens) {
    return palette.tokens[token as PaletteToken];
  }
  // satisfy typescript
  return palette.semantic.text.primary;
}

export interface TypographyProps {
  id?: string;
  children: React.ReactNode;
  color?: PaletteToken | SemanticText | 'inherit';
  variant: TypographyVariant;
  href?: string;
  target?: string;
  rel?: string;
  onClick?: () => void;
  bold?: boolean;
  underlined?: boolean;
  uppercased?: boolean;
  inline?: boolean;
  /**
   * If true, the text will not wrap, but instead will truncate with a text overflow ellipsis.
   * Note that text overflow can only happen with block or inline-block level elements (the element needs to have a width in order to overflow).
   */
  noWrap?: boolean;
  className?: string;
  /**
   * replaces the outer element with a given component
   *
   * IE `<Typography component={"a"}...` becomes `<a>children</a>`
   */
  component?: React.ElementType;
  role?: MuiTypographyProps['role'];
  'data-e2e'?: string;
}

const Typography = React.forwardRef(
  (
    {
      id,
      color,
      children,
      variant,
      onClick,
      href,
      target,
      rel,
      inline,
      bold,
      underlined,
      noWrap,
      uppercased,
      component,
      className,
      role,
      'data-e2e': dataE2e,
    }: TypographyProps,
    ref
  ) => {
    const { classes } = useStyles();
    const foundColor = getColor(color);

    // temp code to avoid inline typography
    // todo: loop back tomorrow
    let tempComponent = component;
    if (
      !tempComponent &&
      (variant === 'h1' ||
        variant === 'h2' ||
        variant === 'h3' ||
        variant === 'h4' ||
        variant === 'h5' ||
        variant === 'pLg' ||
        variant === 'pMd' ||
        variant === 'pSm' ||
        variant === 'pXs' ||
        variant === 'labelMd' ||
        variant === 'labelSm')
    ) {
      if (
        variant === 'pLg' ||
        variant === 'pMd' ||
        variant === 'pSm' ||
        variant === 'pXs'
      ) {
        tempComponent = 'p';
      } else if (variant === 'labelMd' || variant === 'labelSm') {
        tempComponent = 'span';
      } else {
        tempComponent = variant;
      }
    }
    if (!tempComponent) {
      tempComponent = 'span';
    }

    return (
      <TypographyBase
        onClick={onClick}
        variant={variant}
        color={foundColor}
        fontWeight={bold ? weights.bold : weights.regular}
        className={clsx(
          underlined && classes.underlined,
          inline && classes.inline,
          (uppercased || variant === 'labelMd' || variant === 'labelSm') &&
            classes.uppercased,
          className
        )}
        component={tempComponent}
        noWrap={noWrap}
        {...(id && { id })}
        {...(role && { role })}
        {...(href && { href })}
        {...(ref && { ref })}
        {...(target && { target })}
        {...(rel && { rel })}
        {...(dataE2e && { 'data-e2e': dataE2e })}
      >
        {children}
      </TypographyBase>
    );
  }
);

export default Typography;
